import { useCallback, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { toast } from 'react-hot-toast';
import { trpcClient } from '@framework/trpcClient';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
  onDisconnected(): Promise<void>;
};

/**
 * ログイン中ユーザーの Microsoft アカウント連携を解除するモーダル。
 * バックエンド API で解除可否の検証と記録を行った後、Firebase Auth 上の unlink はクライアント SDK で実行する。
 *
 * NOTE: Firebase Auth の unlink をクライアント SDK で行うのは、Admin SDK の providersToUnlink が
 * 対象ユーザーの既存 ID トークンを失効させ auth/user-token-expired で強制サインアウトされてしまうため。
 * クライアント SDK の unlink は自セッションのトークンを維持したまま解除できる。
 */
export const DisconnectMicrosoftModal: React.FC<Props> = ({ isOpen, onClose, userEntity, onDisconnected }: Props) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const handleClose = useCallback(() => {
    setProcessing(false);
    setError('');
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setProcessing(true);
    setError('');

    try {
      const { result } = await trpcClient.user.auth.disconnectMicrosoft.mutate();

      if (result === 'password-not-set') {
        setError(t('auth.microsoftDisconnect.modal.noPasswordWarning'));
        return;
      }

      await FirebaseAuthenticationClient.unlinkMicrosoft();

      await onDisconnected();
      toast.success(t('auth.microsoftDisconnect.modal.disconnectSuccess'));
      handleClose();
    } catch (error) {
      console.error(error);
      setError(t('auth.microsoftDisconnect.modal.disconnectError'));
    } finally {
      setProcessing(false);
    }
  }, [handleClose, onDisconnected, t]);

  const noPassword = !userEntity.hasPassword;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.microsoftDisconnect.modal.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col p-8">
        <div>{t('auth.microsoftDisconnect.modal.description')}</div>

        <div className="py-4 text-center">
          <FlatButton
            color="brand"
            onClick={handleSubmit}
            className="px-8"
            disabled={noPassword || processing}
            loading={processing}
          >
            {t('auth.microsoftDisconnect.modal.submit')}
          </FlatButton>
        </div>

        {noPassword && <div className="text-red-700">{t('auth.microsoftDisconnect.modal.noPasswordWarning')}</div>}

        {error && <div className="pt-8 text-red-700">{error}</div>}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
      </div>
    </Modal>
  );
};
